/**
 * Which tab a provider's post-connect dialog belongs to: Slack's channel
 * picker, or Teams' "Add okthx to Teams" step.
 *
 * The OAuth result reaches every open okthx tab over a BroadcastChannel, and
 * its page takes whatever parameters a link puts on it. Opening the dialog
 * wherever a "connected" result is heard would pop it in every tab, and on a
 * crafted link in a tab that started nothing. So the tab whose Connect button
 * opened the flow marks it here, in memory, which no other tab and no link can
 * set; only that tab opens the dialog, once.
 *
 * "Continue with Slack" on the sign-in page marks Slack too: a new account's
 * sign-in goes on to install okthx, and the install's result is a connect.
 */

/** Longer than any OAuth round trip a person finishes, short enough not to linger. */
const PENDING_TTL_MS = 30 * 60 * 1000;

const startedAt = new Map<string, number>();

/** Record that this tab just sent someone to connect `provider`. */
export function markConnectStarted(provider: string): void {
  startedAt.set(provider, Date.now());
}

/** Whether this tab started a `provider` connect that hasn't come back yet. Clears it. */
export function takeConnectStarted(provider: string): boolean {
  const at = startedAt.get(provider);
  startedAt.delete(provider);
  return at !== undefined && Date.now() - at < PENDING_TTL_MS;
}
